import { dataGridSortDirection } from '@shared/components';
import { parseAsPageNumber } from '@shared/utils';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';

import { type ExamLocationRequestStatus } from '../../constant';

const EXAM_LOCATION_REQUEST_STATUSES = ['IN_REVIEW', 'REJECTED'] as const;

type SortOrder = (typeof dataGridSortDirection)[number];

export default function useAdminExamLocationRequestListSearchParams() {
  const [
    { status, page, pageSize, sortOrder, sortBy, search, province, examCenters },
    setExamLocationRequestListSearchParams,
  ] = useQueryStates({
    status: parseAsStringLiteral(EXAM_LOCATION_REQUEST_STATUSES).withDefault('IN_REVIEW'),
    page: parseAsPageNumber.withDefault(0),
    pageSize: parseAsInteger.withDefault(10),
    sortOrder: parseAsStringLiteral(dataGridSortDirection).withDefault('desc'),
    sortBy: parseAsString,
    search: parseAsString.withDefault(''),
    province: parseAsString.withDefault(''),
    examCenters: parseAsArrayOf(parseAsString).withDefault([]),
  });

  const changePageNumber = (page: number) => {
    setExamLocationRequestListSearchParams({ page });
  };

  const changePageSize = (pageSize: number) => {
    setExamLocationRequestListSearchParams({ pageSize, page: 0 });
  };

  const changeStatus = (status: ExamLocationRequestStatus) => {
    setExamLocationRequestListSearchParams({ status, page: 0, pageSize: 10 });
  };

  const changeSorting = ({
    sortOrder,
    sortBy,
  }: {
    sortOrder: SortOrder | null;
    sortBy: string | null;
  }) => {
    setExamLocationRequestListSearchParams({ sortOrder, page: 0, sortBy });
  };

  const changeSearch = (search: string) => {
    setExamLocationRequestListSearchParams({ search, page: 0 });
  };

  const changeProvince = (province: string) => {
    setExamLocationRequestListSearchParams({ province, page: 0 });
  };

  const changeExamCenters = (examCenters: string[]) => {
    setExamLocationRequestListSearchParams({ examCenters, page: 0 });
  };

  return [
    { status, page, pageSize, sortOrder, sortBy, search, province, examCenters },
    {
      changeStatus,
      changePageNumber,
      changePageSize,
      changeSorting,
      changeSearch,
      changeProvince,
      changeExamCenters,
    },
  ] as const;
}
